import axios from "axios";
import { t } from "i18next";
import apiUrls from "src/api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { IAppLoaderAction } from "src/common/state/loaderHandleMiddleware";
import type { UserFormResults } from "../components/chatState";
import type {
  IGptConversation,
  PlanPhase,
  ScenariaPlan,
  ScenariaPlanSceneGenerationMetadata,
  ScenariaGenerationOptions,
  ScenariaReferenceImageChange,
} from "src/model/model";
import type { RootState } from "src/common/state/store";
import type { IMedia } from "src/model/unityObject";
import { getMedia } from "src/features/gallery/state/gallerySlice";
import { syncUserWithServer } from "src/features/profile/state/profileSlice";
import { resolveDocumentId } from "src/common/util/strapiDocumentId";

const GPT_CONVERSATIONS_PAGE_SIZE = 20;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const errorMessageFromBody = (text: string): string => {
  try {
    const parsed: unknown = JSON.parse(text);
    return isRecord(parsed) && typeof parsed.message === "string" ? parsed.message : text;
  } catch {
    return text;
  }
};

interface GptConversationsFetchResult {
  data: IGptConversation[];
  total: number;
}

const parseGptConversationsFetchResult = (value: unknown): GptConversationsFetchResult => {
  if (!isRecord(value) || !Array.isArray(value.data)) {
    return { data: [], total: 0 };
  }
  return {
    data: value.data as IGptConversation[],
    total: typeof value.total === "number" ? value.total : 0,
  };
};

interface IGptConversationsPagination {
  total: number;
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface IPlanModeProgress {
  status: string | null;
  message: string | null;
  done?: number;
  total?: number;
}

interface IWixarAIStore {
  activeGptConversation?: IGptConversation | null;
  gptConversations: IGptConversation[];
  gptConversationsPagination: IGptConversationsPagination;
  idOfLatestProjectGroupCursedByAi: number | null;
  scenarioHelperContextPdfs: IMedia[];
  scenarioHelperContextImages: IMedia[];
  scenarioHelperRagAgentIds: number[];
  chosenLlmProvider: string;
  lastAiModuleConversationId: number | null;
  lastAiModuleOutputIndex: number | null;
  lastAiModuleGenerateBlockadeImages: boolean;
  planModeProgress: IPlanModeProgress;
  generationStartedAt: number | null;
}

const initialPagination: IGptConversationsPagination = {
  total: 0,
  hasMore: true,
  isLoadingMore: false,
};

const initialPlanModeProgress: IPlanModeProgress = {
  status: null,
  message: null,
};

const adoptServerPhaseIfExitingGeneration = (
  localPhase: PlanPhase | undefined,
  serverPhase: PlanPhase | undefined,
): PlanPhase | undefined =>
  localPhase === "generating" && serverPhase && serverPhase !== "generating"
    ? serverPhase
    : localPhase;

const initialState: IWixarAIStore = {
  activeGptConversation: null,
  gptConversations: [],
  gptConversationsPagination: initialPagination,
  idOfLatestProjectGroupCursedByAi: null,
  scenarioHelperContextPdfs: [],
  scenarioHelperContextImages: [],
  scenarioHelperRagAgentIds: [],
  chosenLlmProvider: "openai",
  lastAiModuleConversationId: null,
  lastAiModuleOutputIndex: null,
  lastAiModuleGenerateBlockadeImages: false,
  planModeProgress: { ...initialPlanModeProgress },
  generationStartedAt: null,
};

export const fetchGptConversations = createAsyncThunk(
  "gpt/fetchGptConversations",
  async ({
    start = 0,
    limit = GPT_CONVERSATIONS_PAGE_SIZE,
  }: { start?: number; limit?: number } & IAppLoaderAction) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(
      `${apiUrls.gpt.findAllWithNestedStuff}?start=${start}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body: unknown = await response.json();
    return parseGptConversationsFetchResult(body);
  },
);

export const fetchGptConversationForProject = createAsyncThunk(
  "gpt/fetchGptConversationForProject",
  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(`${apiUrls.gpt.conversationForProject}/${projectId}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`fetchGptConversationForProject failed: ${response.status}`);
    }
    const body = (await response.json()) as { data?: IGptConversation | null };
    return body.data ?? null;
  },
);

export const fetchMoreGptConversations = createAsyncThunk(
  "gpt/fetchMoreGptConversations",
  async ({ start, limit = GPT_CONVERSATIONS_PAGE_SIZE }: { start: number; limit?: number }) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(
      `${apiUrls.gpt.findAllWithNestedStuff}?start=${start}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body: unknown = await response.json();
    return parseGptConversationsFetchResult(body);
  },
);

export const createChat = createAsyncThunk(
  "gpt/createChat",
  async (
    {
      userPrompt,
      userFormResults,
      sourceMediasWithContext = undefined,
      sourceMedias,
      chosenLlmProvider,
      ragAgentIds = [],
      excludeContextLayers = [],
      orgAiContextOverride = undefined,
      cb,
      cbOnRequestEnd,
    }: {
      userPrompt: string;
      userFormResults: UserFormResults;
      sourceMediasWithContext?: number[];
      sourceMedias: IMedia[];
      chosenLlmProvider: string;
      ragAgentIds?: number[];
      excludeContextLayers?: string[];
      orgAiContextOverride?: { sector?: string; description?: string };
      cb?: (chunk: string) => void;
      cbOnRequestEnd?: () => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.createChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
            userFormResults,
            sourceMediasWithContext,
            service: chosenLlmProvider,
            rag_agent_ids: ragAgentIds,
            excludeContextLayers,
            orgAiContextOverride,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";
      let metaReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (!metaReceived) {
            const sepIdx = chunk.indexOf("\x1E");
            if (sepIdx !== -1) {
              metaReceived = true;
              if (cb && sepIdx > 0) cb(chunk.slice(0, sepIdx));
            } else if (cb) {
              cb(chunk);
            }
          }
        }
      }

      let conversationId: number | undefined;
      const sepIdx = botMessage.indexOf("\x1E");
      if (sepIdx !== -1) {
        try {
          const meta = JSON.parse(botMessage.slice(sepIdx + 1)) as { conversationId?: number };
          conversationId = meta.conversationId;
        } catch (err) {
          console.warn(
            "[chat] unreadable stream metadata, continuing without conversation id:",
            err,
          );
        }
        botMessage = botMessage.slice(0, sepIdx);
      }

      const timestamp = new Date().toISOString();

      const newConversation: IGptConversation = {
        id: conversationId,
        conversation_metadata: {
          model: "gpt-4",
          timestamp,
          userPrompts: [userPrompt],
          userFormResults: userFormResults,
          source_media_with_context: [],
          rag_agent_ids: ragAgentIds,
        },
        conversation_data: {
          output: [botMessage],
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        source_media_with_context: sourceMedias.filter((sm) =>
          (sourceMediasWithContext ?? []).includes(Number(sm.id)),
        ),
      };

      if (cbOnRequestEnd) {
        cbOnRequestEnd();
      }

      return newConversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (createChat)");
    }
  },
);

export const updateChat = createAsyncThunk(
  "gpt/updateChat",
  async (
    {
      userPrompt,
      conversationId,
      ragAgentIds = [],
      cb,
    }: {
      userPrompt: string;
      conversationId: number;
      ragAgentIds?: number[];
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.updateChat, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ userPrompt, conversationId, rag_agent_ids: ragAgentIds }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return { userPrompt, botResponse: botMessage };
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateChat)");
    }
  },
);

export const resetChat = createAsyncThunk(
  "gpt/resetChat",
  async ({ cb }: { cb: () => void } & IAppLoaderAction) => {
    return await axios
      .put<IGptConversation[]>(apiUrls.gpt.resetChat)
      .then((res) => {
        cb();
        return res;
      })
      .catch((e: unknown) => {
        console.error("resetChat did an oopsie: ", e);
        return e;
      });
  },
);

export const updateGptConversationName = createAsyncThunk(
  "gpt/updateName",
  async (
    {
      id,
      newName,
      cb,
    }: {
      id: number;
      newName: string;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      const documentId = await resolveDocumentId(apiUrls.gpt.gptConversations, id);
      return await axios
        .put<IGptConversation>(`${apiUrls.gpt.gptConversations}/${documentId}`, {
          name: newName,
        })
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts :", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateGptConversationName)");
    }
  },
);

export const deleteGptConversation = createAsyncThunk(
  "gpt/deleteName",
  async (
    {
      id,
      cb,
    }: {
      id: number;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      const documentId = await resolveDocumentId(apiUrls.gpt.gptConversations, id);
      return await axios
        .delete<IGptConversation>(`${apiUrls.gpt.gptConversations}/${documentId}`)
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts 299:", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (deleteGptConversation)");
    }
  },
);

export const fetchAndUpdateSpecificConversation = createAsyncThunk(
  "gpt/fetchAndUpdateSpecificConversation",
  async ({ conversationId }: { conversationId: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get<IGptConversation>(
        `${apiUrls.gpt.gptConversations}/${conversationId}`,
      );

      if (!response.data) {
        throw new Error("No conversation data returned");
      }

      return response.data;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (fetchAndUpdateSpecificConversation)");
    }
  },
);

export const duplicatePlanThunk = createAsyncThunk(
  "gpt/duplicatePlanThunk",
  async ({ conversationId }: { conversationId: number }, { rejectWithValue }) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.duplicatePlan, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ conversationId }),
      });
      if (!response.ok) {
        throw new Error(`duplicate-plan ${response.status}`);
      }
      const json = (await response.json()) as { conversation?: IGptConversation };
      if (!json?.conversation) {
        throw new Error("No conversation returned");
      }
      return json.conversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (duplicatePlanThunk)");
    }
  },
);

export const startPlanModeChat = createAsyncThunk(
  "gpt/startPlanModeChat",
  async (
    {
      userPrompt,
      userFormResults,
      sourceMediasWithContext = [],
      sourceMedias,
      chosenLlmProvider,
      ragAgentIds = [],
      excludeContextLayers = [],
      orgAiContextOverride = undefined,
      cb,
      cbOnRequestEnd,
    }: {
      userPrompt: string;
      userFormResults: UserFormResults;
      sourceMediasWithContext?: number[];
      sourceMedias: IMedia[];
      chosenLlmProvider: string;
      ragAgentIds?: number[];
      excludeContextLayers?: string[];
      orgAiContextOverride?: { sector?: string; description?: string };
      cb?: (chunk: string) => void;
      cbOnRequestEnd?: () => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.start, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
            userFormResults,
            sourceMediasWithContext,
            service: chosenLlmProvider,
            rag_agent_ids: ragAgentIds,
            excludeContextLayers,
            orgAiContextOverride,
          },
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";
      let metaReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (!metaReceived) {
            const sepIdx = chunk.indexOf("\x1E");
            if (sepIdx !== -1) {
              metaReceived = true;
              if (cb && sepIdx > 0) cb(chunk.slice(0, sepIdx));
            } else if (cb) {
              cb(chunk);
            }
          }
        }
      }

      let conversationId: number | undefined;
      const sepIdx = botMessage.indexOf("\x1E");
      if (sepIdx !== -1) {
        try {
          const meta = JSON.parse(botMessage.slice(sepIdx + 1)) as { conversationId?: number };
          conversationId = meta.conversationId;
        } catch (err) {
          console.warn("[chat] unreadable stream metadata:", err);
        }
        botMessage = botMessage.slice(0, sepIdx);
      }

      if (!conversationId) {
        throw new Error(t("pages.chat.errorNoConversationId"));
      }

      const timestamp = new Date().toISOString();

      const newConversation: IGptConversation = {
        id: conversationId,
        plan_phase: "intake",
        conversation_metadata: {
          model: chosenLlmProvider === "mistral" ? "mistral" : "gpt-4",
          timestamp,
          userPrompts: [userPrompt],
          userFormResults: userFormResults,
          source_media_with_context: [],
          rag_agent_ids: ragAgentIds,
        },
        conversation_data: {
          output: [botMessage],
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        source_media_with_context: sourceMedias.filter((sm) =>
          sourceMediasWithContext.includes(Number(sm.id)),
        ),
      };

      if (cbOnRequestEnd) {
        cbOnRequestEnd();
      }

      return newConversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (startPlanModeChat)");
    }
  },
);

export const continuePlanModeChat = createAsyncThunk(
  "gpt/continuePlanModeChat",
  async (
    {
      userPrompt,
      conversationId,
      ragAgentIds = [],
      excludeContextLayers = [],
      orgAiContextOverride = undefined,
      sourceMediasWithContext,
      cb,
    }: {
      userPrompt: string;
      conversationId: number;
      ragAgentIds?: number[];
      excludeContextLayers?: string[];
      orgAiContextOverride?: { sector?: string; description?: string };
      sourceMediasWithContext?: number[];
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.continue, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          userPrompt,
          conversationId,
          rag_agent_ids: ragAgentIds,
          excludeContextLayers,
          orgAiContextOverride,
          sourceMediasWithContext,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let botMessage = "";
      let errorMessage: string | null = null;
      let streamDone = false;

      interface PlanModeSseEventData {
        chunk?: unknown;
        message?: unknown;
      }

      const handleEvent = (eventName: string, data: PlanModeSseEventData) => {
        if (eventName === "text") {
          const chunk: string = typeof data.chunk === "string" ? data.chunk : "";
          if (!chunk) return;
          botMessage += chunk;
          if (cb) cb(chunk);
        } else if (eventName === "error") {
          errorMessage = typeof data.message === "string" ? data.message : "stream error";
        }
      };

      while (!streamDone) {
        const { value, done: readerDone } = await reader.read();
        if (readerDone) {
          streamDone = true;
          break;
        }
        if (!value) continue;
        buffer += decoder.decode(value, { stream: true });

        let sepIdx;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);

          let eventName = "message";
          const dataLines: string[] = [];
          for (const line of rawEvent.split("\n")) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
          }
          const dataStr = dataLines.join("\n");
          if (!dataStr) continue;

          let data: PlanModeSseEventData = {};
          try {
            data = JSON.parse(dataStr) as PlanModeSseEventData;
          } catch (e) {
            console.warn("[continuePlanModeChat] SSE data parse failed:", e, dataStr);
            continue;
          }

          handleEvent(eventName, data);
        }
      }

      if (errorMessage) throw new Error(errorMessage);

      return { userPrompt, botResponse: botMessage };
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (continuePlanModeChat)");
    }
  },
);

export const transitionToReady = createAsyncThunk(
  "gpt/transitionToReady",
  async (
    { conversationId, ragAgentIds = [] }: { conversationId: number; ragAgentIds?: number[] },
    { dispatch, rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      dispatch(
        setPlanModeProgress({
          status: "starting",
          message: t("pages.chat.progressPreparingPlan"),
        }),
      );

      const response = await fetch(apiUrls.gpt.planMode.transitionToReady, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ conversationId, rag_agent_ids: ragAgentIds }),
      });

      if (!response.ok && response.status !== 200) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalPlan: ScenariaPlan | null = null;
      let intakeBoundary: number | null = null;
      let errorMessage: string | null = null;

      interface TransitionToReadySseChunk {
        status?: string;
        progress?: { total?: number; done?: number };
        plan?: ScenariaPlan;
        intake_boundary?: number;
        message?: string;
      }

      try {
        let streamDone = false;
        while (!streamDone) {
          const { value, done } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }
          if (!value) continue;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
              const parsed = JSON.parse(trimmed) as TransitionToReadySseChunk;
              if (parsed.status === "describing_images") {
                const total = parsed.progress?.total ?? 0;
                const done = parsed.progress?.done ?? 0;
                dispatch(
                  setPlanModeProgress({
                    status: "describing_images",
                    message:
                      total > 0 ? `Analyse de l'image… (${done}/${total})` : "Analyse des images…",
                    done,
                    total,
                  }),
                );
              } else if (parsed.status === "consolidating_plan") {
                dispatch(
                  setPlanModeProgress({
                    status: "consolidating_plan",
                    message: "Consolidation du plan…",
                  }),
                );
              } else if (parsed.status === "complete") {
                finalPlan = parsed.plan ?? null;
                intakeBoundary =
                  typeof parsed.intake_boundary === "number" ? parsed.intake_boundary : null;
              } else if (parsed.status === "error") {
                errorMessage = parsed.message || "Unknown error";
              }
            } catch (e) {
              console.error("Plan-mode chunk parse failed:", e, "raw:", trimmed);
            }
          }
        }
      } finally {
        reader.cancel().catch(() => undefined);
      }

      if (errorMessage) {
        throw new Error(errorMessage);
      }
      if (!finalPlan) {
        throw new Error("No plan returned");
      }

      return { conversationId, plan: finalPlan, intakeBoundary };
    } catch (error: unknown) {
      console.error("API call failed:", error);
      const message =
        error instanceof Error ? error.message : "API call failed (transitionToReady)";
      return rejectWithValue(message);
    } finally {
      dispatch(resetPlanModeProgress());
    }
  },
);

export const refinePlanThunk = createAsyncThunk(
  "gpt/refinePlanThunk",
  async (
    {
      conversationId,
      message,
      ragAgentIds = [],
      excludeContextLayers = [],
      orgAiContextOverride = undefined,
      referenceImageChange = undefined,
    }: {
      conversationId: number;
      message: string;
      ragAgentIds?: number[];
      excludeContextLayers?: string[];
      orgAiContextOverride?: { sector?: string; description?: string };
      referenceImageChange?: ScenariaReferenceImageChange;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.refine, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          conversationId,
          message,
          rag_agent_ids: ragAgentIds,
          excludeContextLayers,
          orgAiContextOverride,
          ...(referenceImageChange ? { referenceImageChange } : {}),
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(errorMessageFromBody(text) || `HTTP ${response.status}`);
      }

      const body = (await response.json()) as {
        plan?: ScenariaPlan;
        mediaTypeOverrides?: Array<{
          sceneIndex: number;
          sceneId: string;
          sceneTitle: string;
          previousMediaType: "image360" | "video360";
          mediaType: "image360" | "video360";
        }>;
        displayMessage?: string;
      };
      const plan = body.plan;
      if (!plan) throw new Error("No plan returned");
      const mediaTypeOverrides = body.mediaTypeOverrides ?? [];
      const displayMessage = body.displayMessage;
      return { conversationId, message, plan, mediaTypeOverrides, displayMessage };
    } catch (error: unknown) {
      console.error("API call failed:", error);
      const errMessage =
        error instanceof Error ? error.message : "API call failed (refinePlanThunk)";
      return rejectWithValue(errMessage);
    }
  },
);

export const generateFromPlanThunk = createAsyncThunk(
  "gpt/generateFromPlanThunk",
  async (
    {
      conversationId,
      ragAgentIds = [],
      generationOptions,
    }: {
      conversationId: number;
      ragAgentIds?: number[];
      generationOptions: ScenariaGenerationOptions;
    },
    { dispatch, rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.generate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          conversationId,
          rag_agent_ids: ragAgentIds,
          generationOptions,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(errorMessageFromBody(text) || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let result: {
        projectId: number | null;
        projectGroupId: number | null;
        planPhase: PlanPhase;
      } | null = null;
      let errorMessage: string | null = null;

      interface GenerateFromPlanSseData {
        step?: string;
        total?: number;
        done?: number;
        sceneId?: string;
        generationMetadata?: ScenariaPlanSceneGenerationMetadata;
        projectId?: number | null;
        projectGroupId?: number | null;
        planPhase?: PlanPhase;
        message?: string;
      }

      try {
        let streamDone = false;
        while (!streamDone) {
          const { value, done } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }
          if (!value) continue;
          buffer += decoder.decode(value, { stream: true });

          let sepIdx;
          while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);

            let eventName = "message";
            const dataLines: string[] = [];
            for (const line of rawEvent.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
            }
            const dataStr = dataLines.join("\n");
            if (!dataStr) continue;

            let data: GenerateFromPlanSseData = {};
            try {
              data = JSON.parse(dataStr) as GenerateFromPlanSseData;
            } catch (e) {
              console.error("SSE data parse failed:", e, "raw:", dataStr);
              continue;
            }

            if (eventName === "status") {
              const step: string = data.step || "";
              const total = data.total ?? 0;
              const done = data.done ?? 0;
              const stepLabels: { [k: string]: string } = {
                rendering_markdown: t("pages.chat.progressRenderingMarkdown"),
                fetching_metadata: t("pages.chat.progressFetchingMetadata"),
                creating_project: t("pages.chat.progressCreatingProject"),
                generating_images:
                  total > 0
                    ? t("pages.chat.progressGeneratingImages", { count: total })
                    : t("pages.chat.progressPreparingImages"),
                waiting_for_images:
                  total > 0
                    ? t("pages.chat.progressWaitingImages", { done, total })
                    : t("pages.chat.progressWaitingImagesPrep"),
                assigning_images: t("pages.chat.progressAssigningImages"),
              };
              dispatch(
                setPlanModeProgress({
                  status: step || "generating",
                  message: stepLabels[step] || t("pages.chat.progressGenerating"),
                  ...(total > 0 && { done, total }),
                }),
              );
            } else if (
              eventName === "scene_image_submitted" ||
              eventName === "scene_image_complete"
            ) {
              if (data.sceneId && data.generationMetadata) {
                dispatch(
                  mergeSceneGenerationMetadata({
                    sceneId: data.sceneId,
                    generationMetadata: data.generationMetadata,
                  }),
                );
              }
            } else if (eventName === "complete") {
              result = {
                projectId: data.projectId ?? null,
                projectGroupId: data.projectGroupId ?? null,
                planPhase: data.planPhase || "generated",
              };
            } else if (eventName === "error") {
              errorMessage = data.message || "Unknown error";
            }
          }
        }
      } finally {
        reader.cancel().catch(() => undefined);
      }

      if (errorMessage) {
        throw new Error(errorMessage);
      }
      if (!result) {
        throw new Error(t("pages.chat.errorConnectionLost"));
      }

      return { conversationId, ...result };
    } catch (error: unknown) {
      console.error("API call failed:", error);
      const errMessage =
        error instanceof Error ? error.message : "API call failed (generateFromPlanThunk)";
      return rejectWithValue(errMessage);
    } finally {
      dispatch(resetPlanModeProgress());
    }
  },
);

export const chatbotSlice = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    setActiveGptConversation: (
      state: IWixarAIStore,
      action: PayloadAction<IGptConversation | null | undefined>,
    ) => {
      state.activeGptConversation = action.payload;
      state.chosenLlmProvider = action.payload?.conversation_metadata?.model.includes("mistral")
        ? "mistral"
        : "openai";
    },
    resetActiveGptConversation: (state: IWixarAIStore) => {
      state.activeGptConversation = null;
    },
    setIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore, action: PayloadAction<number>) => {
      state.idOfLatestProjectGroupCursedByAi = action.payload;
    },
    resetIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore) => {
      state.idOfLatestProjectGroupCursedByAi = null;
    },
    addScenarioHelperContextPdf: (state: IWixarAIStore, action: PayloadAction<IMedia>) => {
      if (state.scenarioHelperContextPdfs.length >= 5) return;
      if (state.scenarioHelperContextPdfs.some((p) => p.id === action.payload?.id)) return;
      state.scenarioHelperContextPdfs.push(action.payload);
    },
    removeScenarioHelperContextPdf: (state: IWixarAIStore, action: PayloadAction<number>) => {
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.filter(
        (pdf) => pdf.id !== action.payload,
      );
    },
    setScenarioHelperContextPdf: (state: IWixarAIStore, action: PayloadAction<IMedia[]>) => {
      state.scenarioHelperContextPdfs = action.payload;
    },
    resetScenarioHelperContextPdfs: (state: IWixarAIStore) => {
      state.scenarioHelperContextPdfs = [];
    },
    addScenarioHelperContextImage: (state: IWixarAIStore, action: PayloadAction<IMedia>) => {
      if (state.scenarioHelperContextImages.length >= 1) return;
      if (state.scenarioHelperContextImages.some((p) => p.id === action.payload?.id)) return;
      state.scenarioHelperContextImages.push(action.payload);
    },
    removeScenarioHelperContextImage: (state: IWixarAIStore, action: PayloadAction<number>) => {
      state.scenarioHelperContextImages = state.scenarioHelperContextImages.filter(
        (img) => img.id !== action.payload,
      );
    },
    setScenarioHelperContextImages: (state: IWixarAIStore, action: PayloadAction<IMedia[]>) => {
      state.scenarioHelperContextImages = action.payload;
    },
    resetScenarioHelperContextImages: (state: IWixarAIStore) => {
      state.scenarioHelperContextImages = [];
    },
    setPlanModeProgress: (
      state: IWixarAIStore,
      action: PayloadAction<Partial<IPlanModeProgress>>,
    ) => {
      state.planModeProgress = { ...state.planModeProgress, ...action.payload };
    },
    resetPlanModeProgress: (state: IWixarAIStore) => {
      state.planModeProgress = { ...initialPlanModeProgress };
    },
    mergeSceneGenerationMetadata: (
      state: IWixarAIStore,
      action: {
        payload: {
          sceneId: string;
          generationMetadata: ScenariaPlanSceneGenerationMetadata;
        };
      },
    ) => {
      const plan = state.activeGptConversation?.conversation_metadata?.plan;
      if (!plan || !Array.isArray(plan.sceneOutline)) return;
      const idx = plan.sceneOutline.findIndex((s) => s.sceneId === action.payload.sceneId);
      if (idx < 0) return;
      plan.sceneOutline[idx] = {
        ...plan.sceneOutline[idx],
        generationMetadata: {
          ...(plan.sceneOutline[idx].generationMetadata || {}),
          ...action.payload.generationMetadata,
        },
      };
    },
    setScenarioHelperRagAgentIds: (state: IWixarAIStore, action: PayloadAction<number[]>) => {
      state.scenarioHelperRagAgentIds = action.payload;
    },
    resetScenarioHelperRagAgentIds: (state: IWixarAIStore) => {
      state.scenarioHelperRagAgentIds = [];
    },
    setChosenLlmProvider: (state: IWixarAIStore, action: PayloadAction<string>) => {
      state.chosenLlmProvider = action.payload;
    },
    setLastAiModuleContext: (
      state: IWixarAIStore,
      action: {
        payload: {
          conversationId: number;
          relatedGptOutputIndex: number;
          generateBlockadeImages: boolean;
        };
      },
    ) => {
      state.lastAiModuleConversationId = action.payload.conversationId;
      state.lastAiModuleOutputIndex = action.payload.relatedGptOutputIndex;
      state.lastAiModuleGenerateBlockadeImages = action.payload.generateBlockadeImages;
    },
    resetLastAiModuleContext: (state: IWixarAIStore) => {
      state.lastAiModuleConversationId = null;
      state.lastAiModuleOutputIndex = null;
      state.lastAiModuleGenerateBlockadeImages = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGptConversations.fulfilled, (state, { payload }) => {
      state.gptConversations = payload.data;
      const loaded = Array.isArray(payload.data) ? payload.data.length : 0;
      state.gptConversationsPagination = {
        total: payload.total,
        hasMore: loaded < payload.total,
        isLoadingMore: false,
      };

      const active = state.activeGptConversation;
      if (active?.id) {
        const fromList = (payload.data || []).find(
          (c: IGptConversation) => Number(c.id) === Number(active.id),
        );
        const adopted = adoptServerPhaseIfExitingGeneration(
          active.plan_phase ?? undefined,
          fromList?.plan_phase ?? undefined,
        );
        const nextProjectId = active.lastGeneratedProjectId ?? fromList?.lastGeneratedProjectId;
        const nextProjectGroupId =
          active.lastGeneratedProjectGroupId ?? fromList?.lastGeneratedProjectGroupId;
        const phaseChanged = adopted !== active.plan_phase;
        const idsChanged =
          nextProjectId !== active.lastGeneratedProjectId ||
          nextProjectGroupId !== active.lastGeneratedProjectGroupId;
        if (phaseChanged || idsChanged) {
          state.activeGptConversation = {
            ...active,
            plan_phase: adopted,
            lastGeneratedProjectId: nextProjectId,
            lastGeneratedProjectGroupId: nextProjectGroupId,
          };
        }
      }
    });
    builder.addCase(fetchMoreGptConversations.pending, (state) => {
      state.gptConversationsPagination.isLoadingMore = true;
    });
    builder.addCase(fetchMoreGptConversations.fulfilled, (state, { payload }) => {
      const existingIds = new Set(state.gptConversations.map((c) => c.id));
      const newConversations = (payload.data || []).filter(
        (c: IGptConversation) => !existingIds.has(c.id),
      );
      state.gptConversations.push(...newConversations);
      state.gptConversationsPagination = {
        total: payload.total,
        hasMore: state.gptConversations.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchMoreGptConversations.rejected, (state) => {
      state.gptConversationsPagination.isLoadingMore = false;
    });
    builder.addCase(createChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.push(payload);
      state.gptConversationsPagination.total += 1;
    });
    builder.addCase(duplicatePlanThunk.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.unshift(payload);
      state.gptConversationsPagination.total += 1;
    });
    builder.addCase(updateChat.fulfilled, (state, { payload }) => {
      if (state.activeGptConversation) {
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.userPrompt);
        state.activeGptConversation.conversation_data.output.push(payload.botResponse);

        const idx = state.gptConversations.findIndex(
          (c) => c.id === state.activeGptConversation?.id,
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            ...state.activeGptConversation,
          };
        }
      }
    });
    builder.addCase(resetChat.fulfilled, (state) => {
      state.activeGptConversation = null;
      state.gptConversations = [];
      state.gptConversationsPagination = { ...initialPagination };
    });
    builder.addCase(updateGptConversationName.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return payload;
        }
        return conversation;
      });
      state.gptConversations = updatedGptConversationArray;
    });
    builder.addCase(deleteGptConversation.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].filter((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return false;
        }
        return true;
      });
      state.gptConversations = updatedGptConversationArray;
      state.gptConversationsPagination.total = Math.max(
        0,
        state.gptConversationsPagination.total - 1,
      );
      if (Number(state.activeGptConversation?.id) === Number(payload?.id)) {
        state.activeGptConversation = null;
      }
    });
    builder.addCase(fetchAndUpdateSpecificConversation.fulfilled, (state, { payload }) => {
      const preserveIds = (
        existing: IGptConversation | null | undefined,
        incoming: IGptConversation,
      ) => ({
        ...incoming,
        lastGeneratedProjectId:
          incoming?.lastGeneratedProjectId ?? existing?.lastGeneratedProjectId,
        lastGeneratedProjectGroupId:
          incoming?.lastGeneratedProjectGroupId ?? existing?.lastGeneratedProjectGroupId,
      });

      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload.id)) {
          return preserveIds(conversation, payload);
        }
        return conversation;
      });

      state.gptConversations = updatedGptConversationArray;

      if (state.activeGptConversation?.id === payload.id) {
        state.activeGptConversation = preserveIds(state.activeGptConversation, payload);
      }
    });
    builder.addCase(getMedia.fulfilled, (state, { payload }) => {
      const updatedMedia = payload;
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.map((pdf) => {
        return Number(pdf.id) === Number(updatedMedia.id) ? updatedMedia : pdf;
      });
      state.scenarioHelperContextImages = state.scenarioHelperContextImages.map((img) => {
        return Number(img.id) === Number(updatedMedia.id) ? updatedMedia : img;
      });
    });
    builder.addCase(startPlanModeChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.push(payload);
      state.gptConversationsPagination.total += 1;
    });
    builder.addCase(continuePlanModeChat.fulfilled, (state, { payload }) => {
      if (state.activeGptConversation) {
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.userPrompt);
        state.activeGptConversation.conversation_data.output.push(payload.botResponse);
        const idx = state.gptConversations.findIndex(
          (c) => c.id === state.activeGptConversation?.id,
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            ...state.activeGptConversation,
          };
        }
      }
    });
    builder.addCase(transitionToReady.fulfilled, (state, { payload }) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(payload.conversationId)
      ) {
        state.activeGptConversation.plan_phase = "ready_to_generate";
        state.activeGptConversation.conversation_metadata.plan = payload.plan;
        state.activeGptConversation.conversation_metadata.userPrompts.push("");
        state.activeGptConversation.conversation_data.output.push("__PLAN__");
        if (typeof payload.intakeBoundary === "number") {
          state.activeGptConversation.conversation_metadata.intake_boundary =
            payload.intakeBoundary;
        }
        const idx = state.gptConversations.findIndex(
          (c) => Number(c.id) === Number(payload.conversationId),
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            plan_phase: "ready_to_generate",
            conversation_metadata: {
              ...state.gptConversations[idx].conversation_metadata,
              plan: payload.plan,
              ...(typeof payload.intakeBoundary === "number" && {
                intake_boundary: payload.intakeBoundary,
              }),
            },
            conversation_data: {
              ...state.gptConversations[idx].conversation_data,
              output: [
                ...(state.gptConversations[idx].conversation_data?.output || []),
                "__PLAN__",
              ],
            },
          };
        }
      }
    });
    builder.addCase(refinePlanThunk.fulfilled, (state, { payload }) => {
      const persistedPrompt = payload.displayMessage ?? payload.message;
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(payload.conversationId)
      ) {
        state.activeGptConversation.conversation_metadata.plan = payload.plan;
        state.activeGptConversation.conversation_metadata.userPrompts.push(persistedPrompt);
        state.activeGptConversation.conversation_data.output.push("__PLAN__");
        const idx = state.gptConversations.findIndex(
          (c) => Number(c.id) === Number(payload.conversationId),
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            conversation_metadata: {
              ...state.gptConversations[idx].conversation_metadata,
              plan: payload.plan,
              userPrompts: [
                ...(state.gptConversations[idx].conversation_metadata.userPrompts || []),
                persistedPrompt,
              ],
            },
            conversation_data: {
              ...state.gptConversations[idx].conversation_data,
              output: [
                ...(state.gptConversations[idx].conversation_data?.output || []),
                "__PLAN__",
              ],
            },
          };
        }
      }
    });
    builder.addCase(generateFromPlanThunk.pending, (state, action) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(action.meta.arg.conversationId)
      ) {
        state.activeGptConversation.plan_phase = "generating";
        state.generationStartedAt = Date.now();
      }
    });
    builder.addCase(generateFromPlanThunk.fulfilled, (state, { payload }) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(payload.conversationId)
      ) {
        state.activeGptConversation.plan_phase = payload.planPhase;
        state.generationStartedAt = null;
        if (payload.projectId !== null) {
          state.activeGptConversation.lastGeneratedProjectId = payload.projectId;
        }
        if (payload.projectGroupId !== null) {
          state.activeGptConversation.lastGeneratedProjectGroupId = payload.projectGroupId;
        }
        const idx = state.gptConversations.findIndex(
          (c) => Number(c.id) === Number(payload.conversationId),
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            plan_phase: payload.planPhase,
          };
        }
      }
    });
    builder.addCase(generateFromPlanThunk.rejected, (state) => {
      if (state.activeGptConversation && state.activeGptConversation.plan_phase === "generating") {
        state.activeGptConversation.plan_phase = "ready_to_generate";
      }
      state.generationStartedAt = null;
    });
    builder.addCase(syncUserWithServer.fulfilled, (state, { payload }) => {
      const incoming = payload.active_gpt_conversation;

      const localId = state.activeGptConversation?.id;
      const incomingId = incoming?.id;
      const sameConversation =
        localId != null && incomingId != null && Number(localId) === Number(incomingId);

      const canTouchActive = localId == null || sameConversation;

      if (canTouchActive) {
        const active = state.activeGptConversation;
        const localPhase = active?.plan_phase ?? undefined;
        const serverPhase = incoming?.plan_phase ?? undefined;

        const merged: IGptConversation | undefined =
          sameConversation && active && localPhase
            ? ({
                ...incoming,
                plan_phase: adoptServerPhaseIfExitingGeneration(localPhase, serverPhase),
                lastGeneratedProjectId:
                  active.lastGeneratedProjectId ?? incoming?.lastGeneratedProjectId,
                lastGeneratedProjectGroupId:
                  active.lastGeneratedProjectGroupId ?? incoming?.lastGeneratedProjectGroupId,
                conversation_metadata: {
                  ...incoming?.conversation_metadata,
                  plan: active.conversation_metadata?.plan,
                  userPrompts: active.conversation_metadata?.userPrompts,
                  intake_boundary: active.conversation_metadata?.intake_boundary,
                },
                conversation_data: {
                  ...incoming?.conversation_data,
                  output: active.conversation_data?.output,
                },
              } as IGptConversation)
            : incoming;

        state.activeGptConversation = merged;

        const allMedia: IMedia[] = merged?.source_media_with_context ?? [];
        const isImage = (m: IMedia) => (m?.type || "").toLowerCase().startsWith("image");
        state.scenarioHelperContextPdfs = allMedia.filter((m) => !isImage(m));
        state.scenarioHelperContextImages = allMedia.filter((m) => isImage(m));

        if (merged?.id) {
          const idx = state.gptConversations.findIndex((c) => c.id === merged.id);
          if (idx !== -1) {
            state.gptConversations[idx] = merged;
          }
        }
      } else if (incoming && incomingId != null) {
        const idx = state.gptConversations.findIndex((c) => c.id === incomingId);
        if (idx !== -1) {
          state.gptConversations[idx] = incoming;
        }
      }
    });
  },
});

export const chatbotReducer = chatbotSlice.reducer;

export const getActiveGptConversation = (state: RootState) => state.chatbot.activeGptConversation;
export const getIdOfLatestProjectGroupCursedByAi = (state: RootState) =>
  state.chatbot.idOfLatestProjectGroupCursedByAi;
export const getScenarioHelperContextPdfs = (state: RootState) =>
  state.chatbot.scenarioHelperContextPdfs;
export const getScenarioHelperContextImages = (state: RootState) =>
  state.chatbot.scenarioHelperContextImages;
export const getPlanModeProgress = (state: RootState) => state.chatbot.planModeProgress;
export const getGenerationStartedAt = (state: RootState) => state.chatbot.generationStartedAt;
export const getScenarioHelperRagAgentIds = (state: RootState) =>
  state.chatbot.scenarioHelperRagAgentIds;
export const getChosenLlmProvider = (state: RootState) => state.chatbot.chosenLlmProvider;
export const getLastAiModuleContext = (state: RootState) => ({
  conversationId: state.chatbot.lastAiModuleConversationId,
  outputIndex: state.chatbot.lastAiModuleOutputIndex,
  generateBlockadeImages: state.chatbot.lastAiModuleGenerateBlockadeImages,
});

export const getGptConversationsPagination = (state: RootState) =>
  state.chatbot.gptConversationsPagination;

export const getGptConversationsSortedByRecency = (state: RootState) => {
  const sortedConversations = [...state.chatbot.gptConversations].sort((a, b) => {
    const timestampA = new Date(a.updatedAt).getTime();
    const timestampB = new Date(b.updatedAt).getTime();
    return timestampB - timestampA;
  });

  return sortedConversations;
};

export const {
  setActiveGptConversation,
  resetActiveGptConversation,
  setIdOfLatestProjectGroupCursedByAi,
  addScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  setScenarioHelperContextPdf,
  resetScenarioHelperContextPdfs,
  addScenarioHelperContextImage,
  removeScenarioHelperContextImage,
  setScenarioHelperContextImages,
  resetScenarioHelperContextImages,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
  setChosenLlmProvider,
  setLastAiModuleContext,
  resetLastAiModuleContext,
  setPlanModeProgress,
  resetPlanModeProgress,
  mergeSceneGenerationMetadata,
} = chatbotSlice.actions;
